/**
 * ================================================================================
 * Composants de Paiement PayPal - Heartbeats
 * ================================================================================
 * Copyright (c) 2024-2025 Abdelghani Baaloulou - Tous droits réservés
 * ================================================================================
 */

import React, { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Gift, Sparkles, Check, X, ChevronRight, Wine, Heart } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';

// 🔧 FIX: Utiliser l'origine courante pour heartbeats.fr pour éviter les problèmes de config
const getApiUrl = () => {
  const hostname = window.location.hostname;
  // Si on est sur heartbeats.fr, utiliser l'origine directement
  if (hostname === 'heartbeats.fr' || hostname === 'www.heartbeats.fr') {
    return window.location.origin;
  }
  // Sinon utiliser la variable d'environnement
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

const API_URL = getApiUrl();

// ============================================
// 👑 BOUTIQUE COURONNES
// ============================================

export function CrownShop({ isOpen, onClose, customerId, onSuccess }) {
  const [packages, setPackages] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPackages();
    }
  }, [isOpen]);

  const loadPackages = async () => {
    try {
      const [packagesRes, configRes] = await Promise.all([
        fetch(`${API_URL}/api/payments/crowns/packages`),
        fetch(`${API_URL}/api/payments/config`)
      ]);
      
      const packagesData = await packagesRes.json();
      const configData = await configRes.json();
      
      setPackages(packagesData.packages || []);
      setPaypalClientId(configData.paypalClientId || '');
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPack = async (pack) => {
    setSelectedPack(pack);
    setProcessing(true);
    
    try {
      const res = await fetch(`${API_URL}/api/payments/orders/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          itemType: 'crowns',
          itemId: pack.id
        })
      });
      
      const data = await res.json();
      setOrderId(data.orderId);
    } catch (error) {
      toast.error('Erreur lors de la création de la commande');
      setSelectedPack(null);
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = async (paypalOrderId) => {
    try {
      const res = await fetch(`${API_URL}/api/payments/orders/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId,
          paypalOrderId
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message);
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      toast.error('Erreur lors de la confirmation du paiement');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-gradient-to-br from-gray-900 via-purple-900/50 to-gray-900 border-purple-500/30 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Crown className="w-7 h-7 text-yellow-400" />
            Boutique Couronnes
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Crown className="w-10 h-10 text-yellow-400 animate-pulse" />
          </div>
        ) : !selectedPack ? (
          <div className="space-y-4 py-4">
            <p className="text-gray-400 text-center mb-6">
              Les couronnes te permettent d'envoyer des Super Likes 💎, des cadeaux 🎁 et plus encore !
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              {packages.map((pack) => (
                <motion.button
                  key={pack.id}
                  onClick={() => handleSelectPack(pack)}
                  className={`relative p-4 rounded-xl border-2 transition-all ${
                    pack.popular 
                      ? 'border-yellow-500 bg-yellow-500/10' 
                      : 'border-gray-700 bg-gray-800/50 hover:border-purple-500'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {pack.popular && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
                      POPULAIRE
                    </div>
                  )}
                  
                  <div className="text-3xl mb-2">👑</div>
                  <div className="text-2xl font-bold text-yellow-400">
                    {pack.crowns}
                  </div>
                  {pack.bonus > 0 && (
                    <div className="text-sm text-green-400">+{pack.bonus} bonus</div>
                  )}
                  <div className="text-lg font-bold mt-2">{pack.price}€</div>
                  <div className="text-xs text-gray-400">{pack.name}</div>
                </motion.button>
              ))}
            </div>
          </div>
        ) : (
          <div className="py-6 space-y-6">
            <div className="text-center">
              <div className="text-4xl mb-2">👑</div>
              <div className="text-xl font-bold">
                {selectedPack.crowns} Couronnes
                {selectedPack.bonus > 0 && (
                  <span className="text-green-400"> +{selectedPack.bonus}</span>
                )}
              </div>
              <div className="text-2xl font-bold text-yellow-400 mt-2">
                {selectedPack.price}€
              </div>
            </div>

            {paypalClientId && orderId && (
              <PayPalScriptProvider options={{ 
                clientId: paypalClientId,
                currency: 'EUR',
                locale: 'fr_FR'
              }}>
                <PayPalButtons
                  style={{ layout: 'vertical', color: 'gold', shape: 'pill' }}
                  createOrder={(data, actions) => {
                    return actions.order.create({
                      purchase_units: [{
                        amount: {
                          value: selectedPack.price.toString(),
                          currency_code: 'EUR'
                        },
                        description: `${selectedPack.crowns} Couronnes Heartbeats`
                      }]
                    });
                  }}
                  onApprove={async (data, actions) => {
                    const order = await actions.order.capture();
                    handlePaymentSuccess(order.id);
                  }}
                  onError={(err) => {
                    console.error('PayPal error:', err);
                    toast.error('Erreur PayPal');
                  }}
                />
              </PayPalScriptProvider>
            )}

            <Button
              variant="ghost"
              className="w-full text-gray-400"
              onClick={() => {
                setSelectedPack(null);
                setOrderId(null);
              }}
            >
              ← Retour aux packs
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ============================================
// 🍾 BOUTIQUE CADEAUX (Champagne, etc.)
// ============================================

export function GiftShop({ isOpen, onClose, customerId, recipientId, recipientName, onSuccess }) {
  const [gifts, setGifts] = useState([]);
  const [selectedGift, setSelectedGift] = useState(null);
  const [message, setMessage] = useState('');
  const [paypalClientId, setPaypalClientId] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [pickupLocation, setPickupLocation] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadGifts();
    }
  }, [isOpen]);

  const loadGifts = async () => {
    try {
      const [giftsRes, configRes] = await Promise.all([
        fetch(`${API_URL}/api/payments/gifts/catalog`),
        fetch(`${API_URL}/api/payments/config`)
      ]);
      
      const giftsData = await giftsRes.json();
      const configData = await configRes.json();
      
      setGifts(giftsData.gifts || []);
      setPickupLocation(giftsData.pickupLocation || '');
      setPaypalClientId(configData.paypalClientId || '');
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectGift = async (gift) => {
    setSelectedGift(gift);
    setProcessing(true);
    
    try {
      const res = await fetch(`${API_URL}/api/payments/orders/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          itemType: 'gift',
          itemId: gift.id,
          recipientId,
          message
        })
      });
      
      const data = await res.json();
      setOrderId(data.orderId);
    } catch (error) {
      toast.error('Erreur lors de la création de la commande');
      setSelectedGift(null);
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = async (paypalOrderId) => {
    try {
      const res = await fetch(`${API_URL}/api/payments/orders/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId,
          paypalOrderId
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message);
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      toast.error('Erreur lors de la confirmation du paiement');
    }
  };

  const giftCategories = {
    champagne: { icon: '🍾', label: 'Champagne' },
    vin: { icon: '🍷', label: 'Vins' },
    chocolat: { icon: '🍫', label: 'Chocolats' },
    fleurs: { icon: '🌹', label: 'Fleurs' }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-gradient-to-br from-gray-900 via-pink-900/30 to-gray-900 border-pink-500/30 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Gift className="w-7 h-7 text-pink-400" />
            Offrir un Cadeau
          </DialogTitle>
          {recipientName && (
            <p className="text-pink-400 text-sm flex items-center gap-1">
              <Heart className="w-4 h-4" /> Pour {recipientName}
            </p>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Gift className="w-10 h-10 text-pink-400 animate-pulse" />
          </div>
        ) : !selectedGift ? (
          <div className="space-y-4 py-4">
            <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-3 text-sm">
              <p className="text-pink-300">
                🎁 Offre un vrai cadeau ! {recipientName || 'La personne'} pourra le récupérer en boutique.
              </p>
              <p className="text-gray-400 text-xs mt-1">
                📍 {pickupLocation}
              </p>
            </div>

            {/* Message personnalisé */}
            <div>
              <label className="text-sm text-gray-400 block mb-2">Message (optionnel)</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Un petit mot pour accompagner ton cadeau..."
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 resize-none"
                rows={2}
                maxLength={200}
              />
            </div>
            
            {/* Liste des cadeaux */}
            <div className="space-y-2">
              {gifts.map((gift) => (
                <motion.button
                  key={gift.id}
                  onClick={() => handleSelectGift(gift)}
                  className="w-full p-4 rounded-xl border border-gray-700 bg-gray-800/50 hover:border-pink-500 transition-all flex items-center gap-4 text-left"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <div className="text-4xl">{gift.image}</div>
                  <div className="flex-1">
                    <div className="font-bold">{gift.name}</div>
                    <div className="text-sm text-gray-400">{gift.description}</div>
                  </div>
                  <div className="text-xl font-bold text-pink-400">{gift.price}€</div>
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                </motion.button>
              ))}
            </div>
          </div>
        ) : (
          <div className="py-6 space-y-6">
            <div className="text-center">
              <div className="text-6xl mb-3">{selectedGift.image}</div>
              <div className="text-xl font-bold">{selectedGift.name}</div>
              <div className="text-gray-400 text-sm">{selectedGift.description}</div>
              <div className="text-2xl font-bold text-pink-400 mt-3">
                {selectedGift.price}€
              </div>
              {message && (
                <div className="mt-3 p-3 bg-gray-800/50 rounded-lg text-sm italic text-gray-300">
                  "{message}"
                </div>
              )}
            </div>

            {paypalClientId && orderId && (
              <PayPalScriptProvider options={{ 
                clientId: paypalClientId,
                currency: 'EUR',
                locale: 'fr_FR'
              }}>
                <PayPalButtons
                  style={{ layout: 'vertical', color: 'blue', shape: 'pill' }}
                  createOrder={(data, actions) => {
                    return actions.order.create({
                      purchase_units: [{
                        amount: {
                          value: selectedGift.price.toString(),
                          currency_code: 'EUR'
                        },
                        description: `Cadeau: ${selectedGift.name} pour ${recipientName || 'un ami'}`
                      }]
                    });
                  }}
                  onApprove={async (data, actions) => {
                    const order = await actions.order.capture();
                    handlePaymentSuccess(order.id);
                  }}
                  onError={(err) => {
                    console.error('PayPal error:', err);
                    toast.error('Erreur PayPal');
                  }}
                />
              </PayPalScriptProvider>
            )}

            <Button
              variant="ghost"
              className="w-full text-gray-400"
              onClick={() => {
                setSelectedGift(null);
                setOrderId(null);
              }}
            >
              ← Retour aux cadeaux
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ============================================
// 🎟️ MES BONS CADEAUX REÇUS
// ============================================

export function MyGiftVouchers({ isOpen, onClose, customerId }) {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && customerId) {
      loadVouchers();
    }
  }, [isOpen, customerId]);

  const loadVouchers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/gifts/vouchers/${customerId}`);
      const data = await res.json();
      setVouchers(data.vouchers || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_pickup':
        return <span className="px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-xs">À récupérer</span>;
      case 'picked_up':
        return <span className="px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-xs">Récupéré ✓</span>;
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-6 h-6 text-pink-400" />
            Mes Cadeaux Reçus
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Gift className="w-8 h-8 text-pink-400 animate-pulse" />
          </div>
        ) : vouchers.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Tu n'as pas encore reçu de cadeau</p>
            <p className="text-sm">Ça viendra ! 💝</p>
          </div>
        ) : (
          <div className="space-y-3 py-4 max-h-96 overflow-y-auto">
            {vouchers.map((voucher) => (
              <div
                key={voucher.id}
                className={`p-4 rounded-xl border ${
                  voucher.status === 'pending_pickup'
                    ? 'border-yellow-500/50 bg-yellow-500/5'
                    : 'border-gray-700 bg-gray-800/50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-bold">{voucher.giftName}</div>
                    <div className="text-sm text-gray-400">
                      De {voucher.buyerName}
                    </div>
                    {voucher.message && (
                      <div className="text-sm text-gray-300 italic mt-2">
                        "{voucher.message}"
                      </div>
                    )}
                  </div>
                  {getStatusBadge(voucher.status)}
                </div>
                
                {voucher.status === 'pending_pickup' && (
                  <div className="mt-3 p-2 bg-gray-900/50 rounded-lg">
                    <div className="text-xs text-gray-400 mb-1">Code à présenter en boutique :</div>
                    <div className="font-mono text-lg font-bold text-yellow-400">
                      {voucher.code}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default { CrownShop, GiftShop, MyGiftVouchers };
